import { h, ComponentType, FunctionComponent } from "preact";
import { useCallback, useEffect, useState } from "preact/hooks";
import { Router, RoutingResult } from "./router/router";
import { RoutingContext } from "./routing_context";

interface Props {
  router: Router;
  initialRoute: LoadedRoute;
}

export const RouterComponent: FunctionComponent<Props> = (props) => {
  const { router } = props;

  const updateLocation = useCallback(
    async (path: string) => {
      const route = getRoute(router, path);
      setRouteState((prev) => {
        return {
          current: prev.current,
          loading: route,
        };
      });

      const loadedRoute = await loadRoute(route);
      setRouteState((prev) => {
        if (prev.loading !== route) return prev;

        return {
          current: loadedRoute,
          loading: null,
        };
      });
    },
    [router]
  );

  const [routeState, setRouteState] = useState<RouteLoadState>(() => {
    return {
      current: props.initialRoute,
      loading: null,
    };
  });

  const [routingContext] = useState<RoutingContext>(
    () =>
      new RoutingContextImpl({
        updateLocationCallback: (path: string) => {
          updateLocation(path);
        },
      })
  );

  useEffect(() => {
    if (BUILD_PLATFORM !== "browser") return;

    function handlePopState() {
      updateLocation(window.location.pathname);
    }

    window.addEventListener("popstate", handlePopState);
    if ("scrollRestoration" in history) {
      history.scrollRestoration = "manual";
    }

    // clean up
    return () => {
      window.removeEventListener("popstate", handlePopState);
      history.scrollRestoration = "auto";
    };
  }, [updateLocation]);

  const Component = routeState.current.component;
  const routeProps = routeState.current.params;

  return (
    <RoutingContext.Provider value={routingContext}>
      <Component {...routeProps} />
    </RoutingContext.Provider>
  );
};

interface RouteLoadState {
  current: LoadedRoute;
  loading: RoutingResult | null;
}

export function getRoute(router: Router, path?: string): RoutingResult {
  if (BUILD_PLATFORM !== "browser") throw new Error("Must be called in browser only.");

  path = path ?? document.location.pathname;
  const route = router.match(path);
  if (!route) {
    throw new Error("Not found.");
  }

  return route;
}

export async function loadRoute(route: RoutingResult): Promise<LoadedRoute> {
  const component = await route.componentFn();
  return {
    component,
    params: route.params,
  };
}

interface RoutingContextImplInit {
  updateLocationCallback: (path: string) => void;
}

class RoutingContextImpl implements RoutingContext {
  private updateLocationCallback: (path: string) => void;
  constructor({ updateLocationCallback }: RoutingContextImplInit) {
    this.updateLocationCallback = updateLocationCallback;
  }

  push(path: string, state: any) {
    history.pushState(state, "", path);
    this.updateLocationCallback(path);
  }

  replace(path: string, state: any) {
    history.replaceState(state, "", path);
    this.updateLocationCallback(path);
  }
}

export interface LoadedRoute {
  component: ComponentType<any>;
  params?: any;
}
